'use client';

/**
 * Canvas comments — stored in the workspace Y.Doc (NOT Postgres).
 *
 * A workspace IS a content folder whose canvas is a Yjs CRDT doc
 * (content_room_docs); comments live in that same doc so they sync +
 * persist for free, exactly like the vector widget keeps its threads in
 * its own CRDT. Two sibling maps (kept OUT of the items map so they
 * never collide with widget state):
 *   - `commentThreads`  threadId  → { id, x, y, resolved, authorId, authorName, createdAt }
 *   - `commentMessages` messageId → { id, threadId, authorId, authorName, text, reactions, createdAt }
 *
 * Reactions are an inline `{ [emoji]: userId[] }` map on the message —
 * the only mutation a message ever gets after creation, so last-writer-
 * wins on the message value is acceptable (no edit feature to clobber).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOptionalCanvasYjs } from '@/components/workspace/canvas/yjs-mount';

export const COMMENT_EMOJIS = ['👍', '❤️', '🎉', '😂', '🔥', '❓'] as const;

export interface CommentThread {
  id: string;
  x: number;
  y: number;
  resolved: boolean;
  authorId: string;
  authorName: string;
  createdAt: number;
}

export interface CommentMessage {
  id: string;
  threadId: string;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  text: string;
  reactions: Record<string, string[]>;
  createdAt: number;
}

const THREADS_KEY = 'commentThreads';
const MESSAGES_KEY = 'commentMessages';

function newId(): string {
  // Browser crypto — these hooks are client-only.
  return crypto.randomUUID();
}

export interface CanvasCommentsApi {
  available: boolean;
  threads: CommentThread[];
  /** messages for one thread, oldest first. */
  messagesFor: (threadId: string) => CommentMessage[];
  createThread: (x: number, y: number, text: string) => string | null;
  addReply: (threadId: string, text: string) => void;
  toggleReaction: (messageId: string, emoji: string) => void;
  resolveThread: (threadId: string, resolved: boolean) => void;
  deleteMessage: (messageId: string) => void;
  /** my stable user id, for reaction highlighting. */
  myUserId: string | null;
}

export function useCanvasComments(): CanvasCommentsApi {
  const ctx = useOptionalCanvasYjs();
  const doc = ctx?.workspaceRoom.doc ?? null;
  const localUser = ctx?.localUser ?? null;

  const [threads, setThreads] = useState<CommentThread[]>([]);
  const [messages, setMessages] = useState<CommentMessage[]>([]);

  useEffect(() => {
    // Clear on doc change / absence FIRST — otherwise the previous workspace's
    // threads linger in state and render on the next one (e.g. switching into the
    // onboarding workspace, which has no canvas-Yjs doc: `!doc` used to early-return
    // without resetting, so old comments "stuck" across workspaces).
    setThreads([]);
    setMessages([]);
    if (!doc) return;
    const tMap = doc.getMap(THREADS_KEY);
    const mMap = doc.getMap(MESSAGES_KEY);
    const sync = () => {
      setThreads(
        ([...tMap.values()] as CommentThread[]).sort(
          (a, b) => a.createdAt - b.createdAt,
        ),
      );
      setMessages([...mMap.values()] as CommentMessage[]);
    };
    sync();
    tMap.observe(sync);
    mMap.observe(sync);
    return () => {
      tMap.unobserve(sync);
      mMap.unobserve(sync);
    };
  }, [doc]);

  const messagesByThread = useMemo(() => {
    const by = new Map<string, CommentMessage[]>();
    for (const m of messages) {
      const arr = by.get(m.threadId) ?? [];
      arr.push(m);
      by.set(m.threadId, arr);
    }
    for (const arr of by.values()) arr.sort((a, b) => a.createdAt - b.createdAt);
    return by;
  }, [messages]);

  const messagesFor = useCallback(
    (threadId: string) => messagesByThread.get(threadId) ?? [],
    [messagesByThread],
  );

  const createThread = useCallback(
    (x: number, y: number, text: string): string | null => {
      const body = text.trim();
      if (!doc || !localUser || !body) return null;
      const threadId = newId();
      const msgId = newId();
      const now = Date.now();
      doc.transact(() => {
        doc.getMap(THREADS_KEY).set(threadId, {
          id: threadId,
          x,
          y,
          resolved: false,
          authorId: localUser.userId,
          authorName: localUser.username,
          createdAt: now,
        } satisfies CommentThread);
        doc.getMap(MESSAGES_KEY).set(msgId, {
          id: msgId,
          threadId,
          authorId: localUser.userId,
          authorName: localUser.username,
          authorAvatar: localUser.avatarUrl ?? null,
          text: body,
          reactions: {},
          createdAt: now,
        } satisfies CommentMessage);
      });
      return threadId;
    },
    [doc, localUser],
  );

  const addReply = useCallback(
    (threadId: string, text: string) => {
      const body = text.trim();
      if (!doc || !localUser || !body) return;
      const msgId = newId();
      doc.getMap(MESSAGES_KEY).set(msgId, {
        id: msgId,
        threadId,
        authorId: localUser.userId,
        authorName: localUser.username,
        authorAvatar: localUser.avatarUrl ?? null,
        text: body,
        reactions: {},
        createdAt: Date.now(),
      } satisfies CommentMessage);
    },
    [doc, localUser],
  );

  const toggleReaction = useCallback(
    (messageId: string, emoji: string) => {
      if (!doc || !localUser) return;
      const mMap = doc.getMap(MESSAGES_KEY);
      const m = mMap.get(messageId) as CommentMessage | undefined;
      if (!m) return;
      const reactions: Record<string, string[]> = { ...(m.reactions ?? {}) };
      const set = new Set(reactions[emoji] ?? []);
      if (set.has(localUser.userId)) set.delete(localUser.userId);
      else set.add(localUser.userId);
      if (set.size) reactions[emoji] = [...set];
      else delete reactions[emoji];
      mMap.set(messageId, { ...m, reactions });
    },
    [doc, localUser],
  );

  const resolveThread = useCallback(
    (threadId: string, resolved: boolean) => {
      if (!doc) return;
      const tMap = doc.getMap(THREADS_KEY);
      const t = tMap.get(threadId) as CommentThread | undefined;
      if (!t) return;
      tMap.set(threadId, { ...t, resolved });
    },
    [doc],
  );

  const deleteMessage = useCallback(
    (messageId: string) => {
      if (!doc) return;
      const mMap = doc.getMap(MESSAGES_KEY);
      const m = mMap.get(messageId) as CommentMessage | undefined;
      if (!m) return;
      doc.transact(() => {
        mMap.delete(messageId);
        // If that was the thread's last message, drop the thread (+ its pin).
        const remaining = [...mMap.values()].some(
          (x) => (x as CommentMessage).threadId === m.threadId,
        );
        if (!remaining) doc.getMap(THREADS_KEY).delete(m.threadId);
      });
    },
    [doc],
  );

  return {
    available: !!doc && !!localUser,
    threads,
    messagesFor,
    createThread,
    addReply,
    toggleReaction,
    resolveThread,
    deleteMessage,
    myUserId: localUser?.userId ?? null,
  };
}
